"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "@shared/ui.icons";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";

import { Link } from "../Link/Link";
import { Typography } from "../Typography/Typography";

import * as styles from "./StandardCarousel.css";

/**
 * A fully accessible carousel component built with Embla Carousel.
 * Features navigation controls, pagination dots, keyboard navigation, and screen reader support.
 * Follows WAI-ARIA guidelines for carousel/slideshow components.
 *
 * @param title - Optional title displayed above the carousel
 * @param subtitle - Optional subtitle displayed below the title
 * @param linkText - Text for the optional header link
 * @param linkHref - URL for the optional header link (required when linkText is provided)
 * @param children - Carousel slide content (typically an array of slide components)
 * @param showNavigationButtons - Controls visibility of prev/next navigation buttons (default: true)
 * @param showPagination - Controls visibility of pagination dots (default: true)
 * @param onCarouselChange - Callback fired when the active slide changes
 * @param onNavigationClick - Callback fired when navigation buttons are clicked
 * @param onCarouselScrollTo - Callback fired when pagination dots are clicked
 * @param ariaLabel - Accessible label for the carousel container (aria-label)
 * @param ariaLabelledBy - ID of element that labels the carousel (aria-labelledby)
 * @param id - Custom ID for the carousel container (default: "standard-carousel")
 * @param dataTestId - Test ID for automated testing purposes
 * @returns {JSX.Element} The carousel component with all accessibility features
 */
export type StandardCarouselProps = {
  title?: string;
  subtitle?: string;
  linkText?: string;
  linkHref?: string;
  children: React.ReactNode;
  showNavigationButtons?: boolean;
  showPagination?: boolean;
  onCarouselChange?: (index: number) => void;
  onNavigationClick?: (direction: "prev" | "next") => void;
  onCarouselScrollTo?: (index: number) => void;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  id?: string;
  dataTestId?: string;
}

export const StandardCarousel: React.FC<StandardCarouselProps> = ({
  title,
  subtitle,
  linkText,
  linkHref,
  children,
  showNavigationButtons = true,
  showPagination = true,
  onCarouselChange,
  onNavigationClick,
  onCarouselScrollTo,
  ariaLabel,
  ariaLabelledBy,
  id,
  dataTestId
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    skipSnaps: true,
    align: "start"
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const childrenArray = React.Children.toArray(children);
  const totalSlides = childrenArray.length;

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      if (onNavigationClick) onNavigationClick("prev");
    }
  }, [emblaApi, onNavigationClick]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
    if (onNavigationClick) onNavigationClick("next");
  }, [emblaApi, onNavigationClick]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
      if (onCarouselScrollTo) onCarouselScrollTo(index);
    },
    [emblaApi, onCarouselScrollTo]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!emblaApi) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          if (canScrollPrev) scrollPrev();
          break;
        case "ArrowRight":
          event.preventDefault();
          if (canScrollNext) scrollNext();
          break;
        case "Home":
          event.preventDefault();
          scrollTo(0);
          break;
        case "End":
          event.preventDefault();
          scrollTo(totalSlides - 1);
          break;
      }
    },
    [
      emblaApi,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
      scrollTo,
      totalSlides
    ]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
    onSelect();
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (onCarouselChange) {
      onCarouselChange(selectedIndex);
    }
  }, [selectedIndex, onCarouselChange]);

  const renderHeader = title || subtitle || (linkText && linkHref);

  const carouselId = id || "standard-carousel";
  const titleId = title ? `${carouselId}-title` : undefined;

  return (
    <div
      className={styles.carouselContainer}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="application"
      aria-label="Interactive carousel. Use arrow keys to navigate."
      data-testid={dataTestId}
    >
      {renderHeader && (
        <div className={styles.headerSection}>
          <div className={styles.titleSection}>
            {title && (
              <Typography
                variant="subheader-3"
                colour="primary-dark"
                id={titleId}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body-md" colour="primary-medium">
                {subtitle}
              </Typography>
            )}
          </div>

          {linkText && linkHref && (
            <div className={styles.linkSection}>
              <Link text={linkText} href={linkHref} noPadding />
            </div>
          )}
        </div>
      )}

      <div
        className={styles.emblaViewport}
        ref={emblaRef}
        role="region"
        aria-label={ariaLabel || (title ? undefined : "Carousel")}
        aria-labelledby={ariaLabelledBy || titleId}
        aria-live="polite"
        aria-atomic="false"
      >
        <div
          className={styles.emblaContainer}
          role="group"
          aria-label={`${selectedIndex + 1} of ${totalSlides}`}
        >
          {childrenArray.map((child, index) => (
            <div
              className={styles.emblaSlide}
              key={index}
              id={`${carouselId}-slide-${index}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${totalSlides}`}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.navigationSection}>
        {showPagination && (
          <div
            className={styles.paginationSection}
            role="tablist"
            aria-label="Carousel pagination"
          >
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                role="tab"
                title={`Go to slide ${index + 1}`}
                aria-label={`Go to slide ${index + 1}`}
                aria-selected={index === selectedIndex}
                aria-controls={`${carouselId}-slide-${index}`}
                className={
                  index === selectedIndex
                    ? `${styles.paginationDot} ${styles.paginationDotActive}`
                    : styles.paginationDot
                }
                type="button"
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        )}
        {showNavigationButtons && (
          <div className={styles.navigationButtons}>
            <button
              title="Previous slide"
              aria-label="Previous slide"
              className={styles.navButton}
              onClick={scrollPrev}
              type="button"
              disabled={!canScrollPrev}
            >
              <ChevronLeftIcon height={20} width={20} aria-hidden="true" />
            </button>
            <button
              title="Next slide"
              aria-label="Next slide"
              className={styles.navButton}
              onClick={scrollNext}
              type="button"
              disabled={!canScrollNext}
            >
              <ChevronRightIcon height={20} width={20} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
